import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useStytchMemberSession, useStytchMember, useStytchB2BClient } from '@stytch/react/b2b';
import AuthContainer from './AuthContainer';
import NewUserOnboarding from './NewUserOnboarding';
import PendingApproval from './PendingApproval';
import EULAAcceptance from './EULAAcceptance';
import adminService from '../services/AdminService';
import { useSessionJwt } from '../hooks/useSessionJwt';

const ProtectedRoute = ({ children }) => {
  // fromCache: Stytch is revalidating the cached session — treat as
  // authenticated during that window, otherwise the whole app unmounts to the
  // login screen and remounts seconds later (perceived as a full refresh).
  const { session, fromCache } = useStytchMemberSession();
  const { member } = useStytchMember();
  const stytch = useStytchB2BClient();
  const isAuthenticated = !!session || fromCache;
  const { revalidateSession } = useSessionJwt();

  // Deep-link guard: the Stytch cookie can outlive the server-side session,
  // in which case the app renders normally while every API call 401s (page
  // shell with empty data). One forced round-trip on mount settles it — if
  // the session is dead, local state clears and the login screen renders.
  const revalidatedRef = useRef(false);
  useEffect(() => {
    if (isAuthenticated && !revalidatedRef.current) {
      revalidatedRef.current = true;
      revalidateSession();
    }
  }, [isAuthenticated, revalidateSession]);

  // Extract stable member_id to prevent re-renders
  const memberId = useMemo(() => session?.member_id, [session?.member_id]);

  // Track if we've already fetched for this member
  const fetchedMemberRef = useRef(null);

  const [userStatus, setUserStatus] = useState(null);
  const [userProfile, setUserProfile] = useState(null);  // Includes user's eula_version
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Bumped by the Retry button to re-run the user-status fetch.
  const [retryNonce, setRetryNonce] = useState(0);

  // EULA state
  const [eulaConfig, setEulaConfig] = useState(null);  // { version, content_sha256 } from /eula/version
  const [eulaContent, setEulaContent] = useState(null);  // { content, version, content_sha256 } from /eula/content
  const [eulaLoading, setEulaLoading] = useState(false);
  const [eulaContentLoading, setEulaContentLoading] = useState(false);
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const [acceptedEulaVersion, setAcceptedEulaVersion] = useState(null);

  useEffect(() => {
    // Only fetch user status if we have a session
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    // Stytch's heartbeat revalidation transiently yields session=null
    // (fromCache=true, so isAuthenticated stays true here). memberId goes
    // undefined during that window; bailing keeps fetchedMemberRef intact so
    // the fetch isn't repeated when the session (same member) comes back.
    if (!memberId) {
      return;
    }

    // Skip if we've already fetched for this member
    if (fetchedMemberRef.current === memberId) {
      return;
    }

    const requestOrganization = async () => {
      const jwt = stytch?.session.getTokens()?.session_jwt;
      if (!jwt) return null;
      adminService.setAuthToken(jwt);
      return adminService.getOrganization();
    };

    const fetchUserStatus = async () => {
      try {
        let data = await requestOrganization();
        if (data === null) {
          setLoading(false);
          return;
        }
        setUserStatus(data.user_status);
        setUserProfile(data.profile);  // Store profile including eula_version
        setError(null);

        // Mark this member as fetched
        fetchedMemberRef.current = memberId;
        setLoading(false);
      } catch (err) {
        // 401/403 is an AUTH event, not "user not found" — mapping it to
        // not_found flashed the New-User Onboarding screen at logged-out
        // users. Mirror useFetchData: revalidate; if the session is alive
        // (JWT was merely stale) retry once; if dead, KEEP the spinner —
        // revoke clears the session and the !isAuthenticated gate renders
        // the login screen. Never let finally-style code drop loading here,
        // or the shell renders with empty data for a beat.
        if (err?.status === 401 || err?.status === 403) {
          const alive = await revalidateSession();
          if (alive) {
            try {
              const data = await requestOrganization();
              if (data) {
                setUserStatus(data.user_status);
                setUserProfile(data.profile);
                setError(null);
                fetchedMemberRef.current = memberId;
              }
            } catch (retryErr) {
              console.error('Org fetch retry after revalidation failed:', retryErr);
              setError(retryErr.message);
            }
            setLoading(false);
          }
          // Dead session: loading stays true → spinner → login screen.
          return;
        }

        // Non-auth failure (backend down, 5xx, network). This is NOT a
        // statement about the user. Mapping it to not_found rendered the
        // New-User Onboarding form at existing users — during the 2026-08-24
        // outage it asked an org admin to request access, and invited
        // duplicate access requests from people who already had accounts
        // (LA-250). Surface it as a retryable error instead.
        console.error('Failed to fetch user status:', err);
        setError(err.message || 'Request failed');
        setUserStatus(null);
        setLoading(false);
      }
    };

    fetchUserStatus();
  }, [isAuthenticated, memberId, stytch, revalidateSession, retryNonce]);

  // Fetch EULA version for all authenticated users (to check version at login)
  // Runs in parallel with user status fetch - both needed for EULA decision
  useEffect(() => {
    if (isAuthenticated && !eulaConfig && !eulaLoading) {
      setEulaLoading(true);
      const fetchVersion = async () => {
        try {
          const jwt = stytch?.session.getTokens()?.session_jwt;
          if (!jwt) {
            setEulaLoading(false);
            return;
          }
          adminService.setAuthToken(jwt);
          const data = await adminService.getEulaVersion();
          setEulaConfig({ version: data.version, content_sha256: data.content_sha256 });
        } catch (err) {
          console.error('Failed to load EULA version:', err);
          // Fail-open: skip gate on transient error (backend logs it)
          setEulaConfig({ version: null, skip_gate: true });
        } finally {
          setEulaLoading(false);
        }
      };
      fetchVersion();
    }
  }, [isAuthenticated, eulaConfig, eulaLoading, stytch]);

  // Check if user needs to accept EULA (new or updated version)
  const needsEulaAcceptance = () => {
    // Fail-open: skip gate if backend told us to (transient error)
    if (eulaConfig?.skip_gate) return false;
    if (!eulaConfig?.version) return false;  // No EULA configured
    if (eulaAccepted) return false;  // Already accepted in this session

    const userVersion = userProfile?.eula_version;
    const currentVersion = eulaConfig.version;

    // Needs acceptance if: no version recorded OR version doesn't match (string equality)
    return !userVersion || userVersion !== currentVersion;
  };

  // Handle EULA acceptance
  const handleEulaAccept = async (version) => {
    setEulaAccepted(true);
    setAcceptedEulaVersion(version);

    // For existing users in DB, update EULA in backend immediately
    // This includes active, invited, and pending_approval users
    if (userStatus === 'active' || userStatus === 'invited' || userStatus === 'pending_approval') {
      try {
        await adminService.acceptEula(version);
      } catch (err) {
        if (err.status === 409 || err.errorCode === 'version_mismatch') {
          // A new EULA version was published while the gate was open.
          // Re-arm the gate and clear cached version+content so both effects
          // re-fetch — the user must see and accept the NEW text.
          console.warn('EULA version changed mid-session — refetching latest version');
          setEulaAccepted(false);
          setAcceptedEulaVersion(null);
          setEulaContent(null);
          setEulaConfig(null);
          return;
        }
        console.error('Failed to save EULA acceptance:', err);
        // Don't block user - they accepted, we'll try again next time
      }
    }
    // For 'not_found' users, EULA version will be saved during profile completion
  };

  // Handle EULA decline - logout user
  const handleEulaDecline = async () => {
    try {
      await stytch.session.revoke();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Fetch EULA content when gate fires (only once when needed).
  // MUST stay above all early returns — React hooks cannot be conditional
  // (an effect below a conditional return crashes with a hook-order error).
  const gateNeeded = needsEulaAcceptance();
  useEffect(() => {
    if (gateNeeded && !eulaContent && !eulaContentLoading) {
      setEulaContentLoading(true);
      const fetchContent = async () => {
        try {
          const jwt = stytch?.session.getTokens()?.session_jwt;
          if (!jwt) {
            setEulaContentLoading(false);
            return;
          }
          adminService.setAuthToken(jwt);
          const data = await adminService.getEulaContent();
          setEulaContent(data);
        } catch (err) {
          console.error('Failed to load EULA content:', err);
          setEulaContent({ content: '', version: eulaConfig?.version, error: true });
        } finally {
          setEulaContentLoading(false);
        }
      };
      fetchContent();
    }
  }, [gateNeeded, eulaContent, eulaContentLoading, eulaConfig, stytch]);

  // Retry the user-status fetch after a backend failure. Clearing the
  // fetched-member ref is required: the effect short-circuits when it matches.
  const handleRetry = () => {
    fetchedMemberRef.current = null;
    setError(null);
    setLoading(true);
    setRetryNonce((n) => n + 1);
  };

  // Not authenticated - show login
  if (!isAuthenticated) {
    return <AuthContainer />;
  }

  // Loading user status
  if (loading) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary-base mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // Backend failed on a non-auth error. Deliberately NOT the onboarding form:
  // the user's account is almost certainly fine and this is our problem.
  if (error) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="text-6xl mb-6">⚠️</div>

          <h1 className="text-3xl font-bold text-text-primary mb-4">
            Lumoz is temporarily unavailable
          </h1>

          <p className="text-text-secondary mb-2">
            This is usually brief — often maintenance or a deployment in progress.
          </p>

          <p className="text-text-muted text-sm">
            Retry after a few minutes. If it is still unavailable, contact{' '}
            <a href="mailto:support@lumoz.ai" className="underline hover:text-text-secondary">
              support@lumoz.ai
            </a>.
          </p>

          <div className="mt-8 space-y-3">
            <button
              onClick={handleRetry}
              className="w-full bg-accent-primary-base text-text-inverse py-2 px-6 rounded-full font-medium hover:bg-accent-primary-vibrant transition-colors"
            >
              Retry
            </button>
          </div>

          <div className="mt-8 bg-background-secondary border border-border-subtle rounded-lg p-4">
            <p className="text-text-muted text-xs break-words">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Wait for EULA config before rendering any dialogs
  // This ensures needsEulaAcceptance() can make a correct decision
  if (!eulaConfig) {
    console.log('🔍 PROTECTED-ROUTE: Loading EULA config...');
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary-base mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // Check EULA for ALL users (new or existing) - must accept before proceeding
  if (needsEulaAcceptance()) {
    console.log('🔍 PROTECTED-ROUTE: User needs to accept EULA');
    return (
      <EULAAcceptance
        eulaContent={eulaContent?.content}
        eulaVersion={eulaContent?.version || eulaConfig?.version}
        onAccept={handleEulaAccept}
        onDecline={handleEulaDecline}
        loading={eulaContentLoading}
      />
    );
  }

  // JIT user - not in our database yet, needs to complete profile
  if (userStatus === 'not_found') {
    console.log('🔍 PROTECTED-ROUTE: JIT user, EULA accepted, showing onboarding');
    return (
      <NewUserOnboarding
        email={member?.email_address}
        eulaVersion={acceptedEulaVersion}
        onComplete={() => setUserStatus('pending_approval')}
      />
    );
  }

  // Pending approval - waiting for admin
  if (userStatus === 'pending_approval') {
    console.log('🔍 PROTECTED-ROUTE: User pending approval');
    return <PendingApproval />;
  }

  // Invited status - for pre-auth flow users who haven't clicked their invite yet
  // This shouldn't happen often for JIT users
  if (userStatus === 'invited') {
    console.log('🔍 PROTECTED-ROUTE: User is invited but accessing directly');
    // For JIT users who somehow have invited status, treat as active
    // For pre-auth users, they should authenticate via the invite link
  }

  // Active user - full access
  console.log('✅ PROTECTED-ROUTE: User active, showing content');
  return children;
};

export default ProtectedRoute;
